/** All user-facing copy, kept in one place for future localization. */
export const strings = {
  brand: 'ciright universe',
  brandFirst: 'ciright',
  brandSecond: 'universe',
  searchPlaceholder: 'Search the Ciright Universe',
  searchShort: 'Search',
  cursorView: 'View',
  cursorPlay: 'Play',
  searchLabel: 'Search the Ciright Universe',
  sortLabel: 'Sort',
  sorts: {
    relevant: 'Most Relevant',
    newest: 'Newest Added',
    updated: 'Recently Updated',
    az: 'Name: A–Z',
    za: 'Name: Z–A',
  },
  results: (n: number) => `${n.toLocaleString()} ${n === 1 ? 'result' : 'results'}`,
  searching: 'Searching…',
  loading: 'Loading',
  loadingMore: 'Loading more',
  loadMoreFailed: 'Couldn’t load more.',
  endOfList: 'You’ve reached the edge of the universe.',
  noResults: 'No results.',
  noResultsHint: 'Check the spelling or try a different search.',
  unavailable: 'This content is unavailable.',
  requiresAccess: 'This content requires access.',
  requiresAccessHint: 'Sign in with an account that can open it, or ask the owner to share it with you.',
  temporarilyUnavailable: 'Universe is temporarily unavailable.',
  temporarilyUnavailableHint: 'Check your connection, then try again.',
  viewerFailed: 'This content could not be displayed.',
  previewUnsupported: 'This file can’t be previewed here.',
  previewFailed: 'This file couldn’t be previewed.',
  previewTooLarge: 'This file is too large to preview.',
  previewDownloadHint: 'Download it to open it on your device.',
  previewNoDownloadHint: 'Ask the owner for a version that can be viewed online.',
  fileLabel: (ext: string | null) => (ext ? `${ext.toUpperCase()} file` : 'File'),
  textTruncated: (shown: string) => `Showing the first ${shown}. Download the file to see the rest.`,
  rowsTruncated: (rows: number, cols: number) =>
    `Showing the first ${rows.toLocaleString()} rows and ${cols.toLocaleString()} columns. Download the file to see the rest.`,
  sheets: 'Sheets',
  emptySheet: 'This sheet is empty.',
  wrapLines: 'Wrap lines',
  actualSize: 'Actual size',
  fitToScreen: 'Fit to screen',
  tryAgain: 'Try Again',
  clearSearch: 'Clear search',
  shareVia: 'Share via…',
  back: 'Back',
  close: 'Close',
  share: 'Share',
  shareTitle: 'Share this content',
  copyLink: 'Copy Link',
  copied: 'Copied',
  done: 'Done',
  open: 'Open',
  download: 'Download',
  previewNotice: (n: number) => `Showing the first ${n} ${n === 1 ? 'page' : 'pages'}. Sign in for full access.`,
  page: (n: number, total: number) => `${n} / ${total}`,
  pages: (n: number) => `${n.toLocaleString()} ${n === 1 ? 'page' : 'pages'}`,
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  fitWidth: 'Fit width',
  fullscreen: 'Full screen',
  exitFullscreen: 'Exit full screen',
  presentation: 'Present',
  scrollMode: 'Pages',
  previous: 'Previous',
  next: 'Next',
  openExternally: 'Open in new tab',
  openWebsite: 'Open Website',
  sitePreview: 'Preview',
  siteLive: 'Live',
  siteModeLabel: 'Website view',
  capturingPreview: 'Capturing a preview…',
  sitePreviewUnavailable: 'A preview isn’t available for this site.',
  siteLiveHint: 'Some sites block being shown inside other pages. If this stays blank, open the website instead.',
  play: 'Play',
  pause: 'Pause',
  seek: 'Playback position',
  types: {
    pdf: 'PDF',
    presentation: 'Deck',
    video: 'Video',
    audio: 'Audio',
    document: 'Document',
    spreadsheet: 'Spreadsheet',
    link: 'Website',
  },
  updated: (date: string) => `Updated ${date}`,
  signIn: 'Sign In',
  signingIn: 'Signing In…',
  signOut: 'Sign Out',
  signInTitle: 'Sign in with Ciright',
  signInSubtitle: 'Use your Ciright account for full access.',
  username: 'Username',
  password: 'Password',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  keepSignedIn: 'Keep me signed in',
  account: 'Account',
  cancel: 'Cancel',
  signedInAs: (name: string) => `Signed in as ${name}`,
  createAccount: 'Create account',
  untitled: 'Untitled',
  account_: {
    registerTitle: 'Create your Ciright account',
    registerSubtitle: 'One account for Universe and the rest of Ciright.',
    continueWithKeyra: 'Continue with Keyra',
    loadingKeyra: 'Loading Keyra…',
    keyraUnavailable: 'Keyra sign-in isn’t available right now. Use your Ciright account below.',
    orCiright: 'or use your Ciright account',
    devSignIn: 'Continue as local developer',
    devOnly: 'Development only — skips Keyra',
    fullName: 'Full name',
    mobile: 'Mobile',
    mobileNumber: 'Mobile number',
    countryCode: 'Country code',
    mobilePlaceholder: 'Number',
    email: 'Email',
    newPassword: 'At least 8 characters',
    required: 'Required',
    nameRequired: 'Enter your full name.',
    mobileInvalid: 'Enter a mobile number, including the right country code.',
    emailInvalid: 'Enter a valid email address.',
    passwordShort: 'Use at least 8 characters.',
    termsRequired: 'Accept the terms to continue.',
    termsPrefix: 'I agree to the Ciright',
    terms: 'Terms of Service',
    and: 'and',
    privacy: 'Privacy Policy',
    createAccount: 'Create account',
    creating: 'Creating account…',
    createdSignIn: 'Your account was created. Sign in to continue.',
    haveAccount: 'Already have an account?',
    newHere: 'New to Ciright?',
    genericError: 'Something went wrong. Try again.',
    sessionExpired: 'Your session ended. Sign in again to continue.',
  },
  callback: {
    working: 'Completing Keyra sign-in…',
    done: 'Signed in. Taking you back…',
    failed: 'We couldn’t complete your Keyra sign-in.',
    retry: 'Try again',
    home: 'Back to Universe',
  },
  studio: {
    title: 'Cover Studio',
    menu: 'Cover Studio',
    subtitle: 'Better sphere covers from the real content. Nothing is published without approval.',
    forbidden: 'Cover Studio is limited to publishers.',
    notConfigured: 'Not configured',
    notConfiguredBody: 'No image provider key is set on the server, so covers can’t be generated. Existing covers keep showing.',
    searchPlaceholder: 'Find an item by title',
    noResults: (q: string) => `No items match “${q}”.`,
    noItems: 'No items to show.',
    pickItem: 'Choose an item to work on.',
    original: 'Original',
    published: 'Published',
    candidate: 'Candidate',
    approved: 'Approved',
    rejected: 'Rejected',
    retired: 'Retired',
    treatment: 'Treatment',
    treatments: {
      source_faithful: 'Source faithful',
      enhanced_composition: 'Enhanced composition',
      concept_illustration: 'Concept illustration',
    },
    treatmentHelp: {
      source_faithful: 'The real page or frame, with a better crop and lighting.',
      enhanced_composition: 'The real subject with generated surroundings.',
      concept_illustration: 'A fully generated scene. Approve only where it can’t be mistaken for a real product, place or event.',
    },
    source: 'Source image',
    sources: { thumbnail: 'Current thumbnail', cover: 'Cover image', upload: 'Upload an image' },
    uploadFile: 'Image file (PNG, JPEG or WebP)',
    uploadFailed: 'Couldn’t read that file. Try another image.',
    guidance: 'Guidance (optional)',
    guidancePlaceholder: 'e.g. warmer light, more space around the subject',
    privateConfirm: 'The owner has authorized sending this private content to the image provider.',
    generate: 'Generate cover',
    generating: 'Generating…',
    regenerate: 'Generate another version',
    approve: 'Approve',
    reject: 'Reject',
    publish: 'Publish',
    revert: 'Revert to original',
    focus: 'Focus and zoom',
    focusX: 'X',
    focusY: 'Y',
    focusZoom: 'Zoom',
    saveFocus: 'Save focus',
    versionsLeft: (n: number) => `${n} ${n === 1 ? 'version' : 'versions'} left on this request`,
    deduplicated: 'This exact request already exists, so it was reopened instead of generating again.',
    noJobs: 'No covers have been generated for this item yet.',
    back: 'Back to Universe',
  },
} as const;
